<div class="card h-100 event-card">
  <!-- Event Image -->
  <div class="event-image-container position-relative">
    @if (event.header_url) {
      <img [src]="event.header_url" class="card-img-top event-image" [alt]="event.event_name"/>
    } @else {
      <div class="event-image-placeholder bg-light d-flex align-items-center justify-content-center">
        <i class="fa fa-calendar fs-3x text-muted"></i>
      </div>
    }

    <!-- Event Status Badge -->
    <div class="position-absolute top-0 end-0 m-3">
      @if (event.status === 'cancelled') {
        <span class="badge badge-danger badge-lg">{{ 'eventCard.cancelled' | translate }}</span>
      } @else if (isEventUpcoming()) {
        <span class="badge badge-success badge-lg">{{ 'eventCard.upcoming' | translate }}</span>
      } @else {
        <span class="badge badge-secondary badge-lg">{{ 'eventCard.past' | translate }}</span>
      }
    </div>
  </div>

  <div class="card-body d-flex flex-column p-9">
    <!-- Event Category Badge -->
    <div class="mb-3">
      <span class="badge" [ngClass]="getCategoryBadgeClass()">{{ getCategoryDisplay() }}</span>
    </div>

    <!-- Event Name -->
    <h3 class="card-title fw-bold fs-4 mb-2">{{ event.event_name }}</h3>

    <!-- Organiser -->
    @if (event.company) {
      <div class="mb-3">
        @if (event.company.is_personal) {
          <span class="text-muted fs-7">
            <i class="fa fa-user me-1"></i>{{ event.company.company_name }}
          </span>
        } @else {
          <a class="text-muted fs-7 text-decoration-none organiser-link" (click)="navigateToOrganisation($event)">
            <i class="fa fa-building me-1"></i>{{ event.company.company_name }}
          </a>
        }
      </div>
    }

    <!-- Event Details -->
    <div class="mb-5">
      <div class="row g-3">
        <!-- Left Column -->
        <div class="col-6">
          <!-- Dates -->
          @if (event.start_date) {
            <div class="d-flex align-items-start mb-3">
              <i class="fa fa-calendar text-primary me-2 fs-6 mt-1"></i>
              <div>
                <div class="fw-semibold fs-7">{{ formatDateDMYDots(event.start_date) }}</div>
                @if (event.end_date && event.start_date !== event.end_date) {
                  <div class="text-muted fs-8">{{ 'eventCard.to' | translate }} {{ formatDateDMYDots(event.end_date) }}</div>
                }
              </div>
            </div>
          }

          <!-- Location -->
          @if (event.city) {
            <div class="d-flex align-items-start mb-3">
              <i class="fa fa-location-dot text-primary me-2 fs-6 mt-1"></i>
              <div>
                <div class="fw-semibold fs-7">{{ event.city }}{{ event.country ? ', ' + event.country : '' }}</div>
                @if (event.address) {
                  <a [href]="getGoogleMapsUrl()" target="_blank" class="text-muted fs-8 address-link text-decoration-none">
                    {{ event.address }}
                    <i class="fa fa-external-link ms-1 fs-9"></i>
                  </a>
                }
              </div>
            </div>
          }

          <!-- Registration Deadline -->
          @if (event.registration_deadline) {
            <div class="d-flex align-items-start">
              <i class="fa fa-clock text-warning me-2 fs-6 mt-1"></i>
              <div>
                <div class="text-muted fs-8">{{ 'eventCard.regDeadline' | translate }}</div>
                <div class="fw-semibold fs-7">{{ formatDateDMYDots(event.registration_deadline) }}</div>
              </div>
            </div>
          }
        </div>

        <!-- Right Column -->
        <div class="col-6">
          <!-- Status -->
          <div class="d-flex align-items-start mb-3">
            <i class="fa me-2 fs-6 mt-1"
               [class.fa-circle-check]="event.status !== 'cancelled'"
               [class.fa-ban]="event.status === 'cancelled'"
               [class.text-success]="event.status === 'published'"
               [class.text-danger]="event.status === 'cancelled'"
               [class.text-muted]="event.status === 'draft'"></i>
            <div>
              <div class="fw-semibold fs-7">{{ 'eventCard.status' | translate }}</div>
              <div class="fs-8"
                   [class.text-success]="event.status === 'published'"
                   [class.text-danger]="event.status === 'cancelled'"
                   [class.text-muted]="event.status === 'draft'">
                @if (event.status === 'cancelled') {
                  {{ 'eventCard.cancelled' | translate }}
                } @else if (event.status === 'published') {
                  {{ 'eventCard.published' | translate }}
                } @else {
                  {{ 'eventCard.draft' | translate }}
                }
              </div>
            </div>
          </div>

          <!-- Payment Status -->
          <div class="d-flex align-items-start">
            <i class="fa fa-credit-card me-2 fs-6 mt-1" [class.text-success]="event.payment_enabled" [class.text-muted]="!event.payment_enabled"></i>
            <div>
              <div class="fw-semibold fs-7">{{ 'eventCard.payment' | translate }}</div>
              <div class="fs-8" [class.text-success]="event.payment_enabled" [class.text-muted]="!event.payment_enabled">
                {{ event.payment_enabled ? ('eventCard.enabled' | translate) : ('eventCard.disabled' | translate) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="mt-auto d-flex gap-2 button-row">
      <app-button
        [label]="'eventCard.manage' | translate"
        [variant]="'primary'"
        [iconClass]="'fa fa-edit'"
        class="button-half"
        (clicked)="navigateToManageEvent(event.event_id)"
      />
      <app-button
        [label]="'eventCard.view' | translate"
        [variant]="'outline'"
        [iconClass]="'fa fa-eye'"
        class="button-half"
        [routerLink]="'/event-details/' + event.event_id"
      />
      @if (showDelete) {
        <div class="dropdown">
          <button
            class="btn btn-light btn-icon"
            type="button"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <i class="fa fa-ellipsis-v"></i>
          </button>
          <ul class="dropdown-menu dropdown-menu-end">
            <li>
              <a class="dropdown-item text-danger" (click)="onDeleteClick()">
                <i class="fa fa-trash me-2"></i>{{ 'eventCard.deleteEvent' | translate }}
              </a>
            </li>
          </ul>
        </div>
      }
    </div>
  </div>
</div>
